import { logWarning } from '@core/logging';
import { AuthContext } from '@core/shared';
import { apiOf, dropIdentity, OptionsApi, scopeOf, SESSION_QUERY_OPTIONS, TokenResponse, writeSession } from '@core/swagger-client';
import { LoaderPage } from '@core/ui';
import { useRouter } from '@online-webinar/router';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Fragment, ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const queryClient = useQueryClient();
    const router = useRouter();
    // This runtime's Api. On the server it was created with the request's own credentials and is
    // left alone; in the browser this provider is the one place that keeps its token current.
    const api = apiOf(queryClient);

    // The session is the one source of who is signed in; everything below derives from it, so the
    // rendered identity, the AuthContext and the cache scope can never disagree. During SSR it
    // resolves through /login/v2/me, which answers with the same payload minus the token, because
    // the query result is serialised into the rendered page. In the browser it resolves through
    // /login/v2/refresh, which carries the token and renews the AuthSession cookie.
    // refetchOnMount: 'always' is what makes that work: the SSR answer hydrates into this entry
    // without a token, and without a refetch the client would sit on it until it went stale.
    const { data: session, refetch, dataUpdatedAt } = useSuspenseQuery(OptionsApi.login.session(SESSION_QUERY_OPTIONS));

    // Whether the session entry has been written on this side since the first render: by this
    // client's own refresh, or by a login, logout or switch below. Only then does the session say
    // anything about the browser's stored token. A refresh that fails does not count -- it says the
    // server could not be reached, not that the token is dead -- and dataUpdatedAt moves on a
    // successful answer only, never on an error.
    const initialDataUpdatedAt = useRef(dataUpdatedAt);
    const answeredHere = dataUpdatedAt !== initialDataUpdatedAt.current;

    // SSR deliberately hands over no token, so on the client the stored one is the starting point
    // until the refetch above returns a fresh one.
    const [storedToken] = useState<string | null>(() => (import.meta.env.SSR ? null : globalThis.localStorage.getItem('authorizationToken')));
    // SSR hands over no token, and its /me answer comes from a cookie that a cross-site navigation does
    // not send (AuthSession is SameSite=Strict), so an anonymous server render says nothing about the
    // browser's own credential. The stored token therefore stays in use until this client's refresh has
    // answered -- with that token as bearer, which is what makes the answer authoritative -- and while
    // the session is signed in but the server could not hand a token over. Never render from `token`:
    // it differs between server and browser by design and would break hydration.
    const token = session.token ?? (session.isAuthenticated || !answeredHere ? storedToken : null);
    const rights = session.rights ?? [];
    const scope = scopeOf(session);

    if (!import.meta.env.SSR) {
        // During render, not in an effect: a child that mounts below may fetch during its own render.
        api.setSecurityData(token);
    }

    useEffect(() => {
        if (token) {
            globalThis.localStorage.setItem('authorizationToken', token);
        } else if (answeredHere && !session.isAuthenticated) {
            // No token *and* no session, from an answer on this side: a stored one is worthless
            // and goes. Not having a token while the session is valid is a normal state - a first
            // load, before the refresh has returned - and so is a server render that came out
            // anonymous, or a refresh that failed; none of those may throw the stored credential away.
            globalThis.localStorage.removeItem('authorizationToken');
        }
    }, [token, session.isAuthenticated, answeredHere]);

    // The identity transition. Whatever changed the session -- a login or switch mutation, a
    // logout, the mount or interval refetch, the session ending in another tab -- the cache of the
    // previous identity goes and the route data reloads for the new one. It runs after the commit
    // that remounted the tree below (see the key on the Fragment), so no observer of the old
    // identity is left.
    const renderedScope = useRef(scope);
    useEffect(() => {
        if (renderedScope.current === scope) return;
        dropIdentity(queryClient, renderedScope.current);
        renderedScope.current = scope;
        void router.invalidate();
    }, [scope, queryClient, router]);

    // Being signed out arrives either as isAuthenticated:false or as a session without a token
    // (see LoginV2Controller.RefreshToken); both mean "nobody".
    const updateAuthState = useCallback(
        (data: TokenResponse) => {
            const next = data.isAuthenticated && data.token ? data : { ...data, isAuthenticated: false };
            // The credential moves with the session, in the same tick. Every flow that calls this
            // navigates right away, and the loaders of the next route hash their queries under the
            // new identity the moment the session is in the cache; a render later is too late, the
            // first of those queries would still carry the previous bearer and its answer would be
            // cached under the new identity.
            if (!import.meta.env.SSR) {
                api.setSecurityData(next.token);
            }
            writeSession(queryClient, next);
        },
        [api, queryClient]
    );

    const refreshToken = useCallback(async () => {
        // A refetch that fails leaves the current session in place: an exception here means the
        // server could not be reached or errored - a rolling deploy, a dropped connection, a 5xx -
        // which says nothing about the session. Wiping it turned every such blip into a forced
        // re-login.
        const result = await refetch();
        if (result.isError) {
            logWarning('Refreshing the session failed ({Error}); keeping the current session', {
                Error: result.error.message ?? String(result.error.statusCode)
            });
        }
    }, [refetch]);

    // Memoize the auth context value to prevent unnecessary re-renders of consuming components when the auth state changes.
    const authContextValue = useMemo(() => {
        const has = (right: string) => rights.includes(right);
        return {
            token,

            isAuthenticated: session.isAuthenticated,
            isVerified: !!session.mainAccount?.isVerified,
            isMainAccount: !!session.mainAccount && session.mainAccount.accountId === session.activeAccount?.accountId,

            mainAccount: session.mainAccount,
            activeAccount: session.activeAccount,
            managedAccounts: session.managedAccounts || [],

            hasRights: rights.length > 0,
            rights,
            canBroadcast: has('CanBroadcast'),
            canInviteAccounts: has('CanInviteAccounts'),
            canManageTranslations: has('CanManageTranslations'),
            canManageEmailStyle: has('CanManageEmailStyle'),
            canManageEmailTemplates: has('CanManageEmailTemplates'),
            canManageEmailLists: has('CanManageEmailLists'),
            canManageBlacklist: has('CanManageBlacklist'),
            canManageChatLinkDomains: has('CanManageChatLinkDomains'),
            canViewUsageStatistics: has('CanViewUsageStatistics'),

            refreshToken,
            updateAuthState
        };
    }, [token, session, rights, refreshToken, updateAuthState]);

    // Everything below is rendered for one identity. A new scope remounts it, so every component
    // hashes its queries again and no local state from the previous account survives. The fallback
    // is for a remount that suspends: the root route has no boundary of its own under SSR.
    //
    // The key is the identity, never the token. A renewed token (the 30-minute refresh, a
    // refreshToken() call) leaves the scope as it is and must not remount: the live pages leave the
    // webinar when they unmount, so a remount would tear down a running broadcast.
    return (
        <AuthContext.Provider value={authContextValue}>
            <Suspense fallback={<LoaderPage />}>
                <Fragment key={scope}>{children}</Fragment>
            </Suspense>
        </AuthContext.Provider>
    );
};
